import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { QueryDropdown } from 'basic-components';
import Query from 'dashboard-services/api/Query';

import { creatorSeriesActions } from 'actions/series';
import getApiConfig from 'api/ApiConfig';
import { dataStorageQueries } from 'queries/datastorage';

import DropdownIndicator from './DropdownIndicator';

import './UserDefinedFilter.scss';

const dropdownComponents = {
  DropdownIndicator
}

const useSearchSymbolGroups = ({ query, groupName, symbolsFilter, metadatasFilter }) => {
  const dispatch = useDispatch(),
        buildedQuery = creatorSeriesActions.getSearchQuery({ query, symbolsFilter, metadatasFilter, groupName }),
        onSuccess = useCallback(response => dispatch(creatorSeriesActions.onEnvsSuccess(response)),[dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withFetching(false)
            .withOnSuccess(onSuccess)
        , [dispatch, onSuccess]),
        select = useCallback(data => data?.pages?.flatMap(page => page?.items)?.map(item => ({ value: item.groupName, label: item.groupName })), []),
        { data: symbolGroups, isFetching, fetchNextPage, isFetchingNextPage } = dataStorageQueries.useSearchSymbolsEnvs({
          getQueryBuilder,
          query: buildedQuery,
          collapse: "groupName",
          sortBy: "groupName.keyword=asc",
          withValuesOnly: true,
          shouldRefetch: false,
          select
        })

  return { symbolGroups, isFetching, isFetchingNextPage, fetchNextPage };
}

const UserDefinedGroupFilter = ({ filterKey, label, value = undefined, setValue, symbols = undefined, metadatas = undefined, groupFilter = undefined, index }) => {
  const [inputQuery, setInputQuery] = useState(""),
        { 
          symbolGroups,
          isFetching,
          isFetchingNextPage,
          fetchNextPage,
        } = useSearchSymbolGroups({
          query: undefined,  // other dropdowns does not support it
          groupName: inputQuery,
          symbolsFilter: symbols,
          metadatasFilter: metadatas,
        }),
        onChange = useCallback(value => setValue({ value, name: filterKey }), [filterKey, setValue]),
        dropdownRef = useRef()

  useEffect(() => {
    dropdownRef.current?.refreshOptions?.()
  }, [groupFilter, metadatas, symbols])

  useEffect(() => {
    if(!value) {
      dropdownRef.current?.dropdown?.current?.setValue(value)
    }
  }, [value])

  return (
    <div className="user-defined-filter">
      <QueryDropdown
          className="user-defined-filter__dropdown"
          components={dropdownComponents}
          creatable
          defaultValue={value}
          fetchNextPage={fetchNextPage}
          fixed
          isFetching={isFetching || isFetchingNextPage}
          key={index + filterKey}
          menuPlacement="bottom"
          menuPortalTarget={document.querySelector('.ng-office-app__authed__content--series')}
          name={filterKey}
          noError
          onChange={onChange}
          onInputChange={setInputQuery}
          options={symbolGroups}
          placeholder={label}
          ref={dropdownRef}
          variant="border"
      />
    </div>
  )
}

UserDefinedGroupFilter.propTypes = {
  filterKey: PropTypes.string.isRequired,
  groupFilter: PropTypes.string,
  index: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  metadatas: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  symbols: PropTypes.object,
  value: PropTypes.string
}

export default UserDefinedGroupFilter;